// FILE: src/components/FoundingTesterLanding.js
//
// Public Founding Tester / Beta program landing page.
//
// This is the FIRST unauthenticated surface in the app: it requires no learner
// auth context and renders for anonymous visitors arriving from Reddit,
// LinkedIn, study groups, instructor referrals, and direct invitations.
// App.js gates it behind view === 'founding-tester' (deep-linkable via
// ?view=founding-tester) and a page_view telemetry event fires automatically
// on mount via App.js's view effect.
//
// CTAs:
//   • "Join the Beta" / "Create Free Account" → dispatch 'pm-pixel:auth' so the
//     HeaderUserMenu (sibling of App) opens the LearnerAuth modal on the Sign-up
//     tab. No auth state is read or required here.
//   • "Join Discord" → external community invite (REACT_APP_DISCORD_INVITE_URL,
//     falling back to the hardcoded invite).
//
// Designed for future-proofing: testimonials / user counts / success stories /
// beta metrics drop into their own <section> blocks without a redesign.
//
// No Redux. Functional component, hooks only.

import React from 'react';
import PMPixelLogo from '../branding/PMPixelLogo';
import CoachAvatar from '../coach/CoachAvatar';
import { trackEvent } from '../../services/telemetryService';
import styles from './FoundingTesterLanding.module.css';

const DISCORD_URL =
  process.env.REACT_APP_DISCORD_INVITE_URL || 'https://discord.gg/QDhzGYkxub';

const FEATURES = [
  { icon: '📝', name: 'Practice Exams',       desc: 'Realistic, blueprint-aligned questions.' },
  { icon: '🧪', name: 'Full-Length Simulations', desc: 'Exam-day timing and structure.' },
  { icon: '📊', name: 'Readiness Tracking',    desc: 'Know when you are truly exam ready.' },
  { icon: '🎯', name: 'Targeted Practice',     desc: 'Drill the areas that need work.' },
  { icon: '📈', name: 'Performance Insights',  desc: 'See strengths and weaknesses clearly.' },
  { icon: '🗺️', name: 'Personalized Study Plans', desc: 'A path built around your exam date.' },
];

const CHALLENGES = [
  'Knowing if they are truly exam ready',
  'Finding realistic practice questions',
  'Understanding their strengths and weaknesses',
  'Building an effective study plan',
];

const FEEDBACK_AREAS = [
  'User experience',
  'Readiness guidance',
  'Practice exams',
  'Performance analytics',
  'Study planning recommendations',
  'Certification-specific content',
];

const CREDENTIALS = [
  'PMI Authorized Training Partner Instructor',
  'PMBOK Contributor',
  'Author',
  'Exam Question Author',
  'Program & Project Management Practitioner',
];

export default function FoundingTesterLanding() {
  // Enrollment closed (2026-08): this page is preserved as a historical
  // record — the URL has been shared publicly and must not break. The old
  // recruitment CTAs now route to the Founders Wall instead of signup.
  function openWall() {
    trackEvent('founding_tester_cta', 'growth', { feature: 'founding-tester', target: 'wall' });
    try {
      window.dispatchEvent(new CustomEvent('pm-pixel:navigate', { detail: { view: 'founding-testers' } }));
    } catch (_) { /* CustomEvent unavailable — non-fatal */ }
  }

  function trackDiscord() {
    trackEvent('founding_tester_cta', 'growth', { feature: 'founding-tester', target: 'discord' });
  }

  return (
    <div className={styles.page} data-testid="founding-tester-landing">
      <div className={styles.inner}>

        {/* ── Hero ───────────────────────────────────────────────────────── */}
        <header className={styles.hero}>
          <div>
            <div className={styles.heroLogo}>
              <PMPixelLogo size="large" decorative={false} />
            </div>
            <div className={styles.heroBadgeRow}>
              <span className={styles.foundingPill}>★ Founding Tester Program — Class of 2026</span>
            </div>
            <h1 className={styles.heroTitle}>
              The PM Pixel <span>Founding Testers</span>
            </h1>
            <p className={styles.heroSub}>
              During the 2026 beta, a small community of early learners tested
              PM Pixel, reported what broke, and shaped what it became. The
              program is now complete and enrollment is closed — this page
              stays up in their honor.
            </p>
            <p className={styles.heroSub} data-testid="ft-enrollment-closed">
              <strong>Enrollment closed.</strong> The founding cohort&apos;s names
              are permanently etched on the Founders Wall.
            </p>
            <div className={styles.heroCtas}>
              <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={openWall} data-testid="ft-visit-wall">
                Meet the Founding Testers →
              </button>
              <a className={`${styles.btn} ${styles.btnDiscord}`} href={DISCORD_URL} target="_blank" rel="noopener noreferrer" onClick={trackDiscord} data-testid="ft-join-discord">
                💬 Join Discord
              </a>
            </div>
          </div>
          <div className={styles.heroArt}>
            <img className={styles.heroBadgeSticker} src="/themes/pm-pixel/badges/Beta-clean.png" alt="" aria-hidden="true" />
            <CoachAvatar pose="thumbsup" heightPx={500} loading="eager" />
          </div>
        </header>

        {/* ── How participation works (directly under the hero) ──────────── */}
        <section className={styles.section}>
          <p className={styles.eyebrow}>How participation works</p>
          <h2 className={styles.sectionTitle}>Three simple steps</h2>
          <div className={styles.grid3}>
            <div className={styles.stepCard}>
              <span className={styles.stepNum}>1</span>
              <h3 className={styles.stepTitle}>Create an account</h3>
              <p className={styles.stepText}>Free to join — no payment, no commitment.</p>
            </div>
            <div className={styles.stepCard}>
              <span className={styles.stepNum}>2</span>
              <h3 className={styles.stepTitle}>Use PM Pixel</h3>
              <p className={styles.stepText}>Study with it during your real certification preparation.</p>
            </div>
            <div className={styles.stepCard}>
              <span className={styles.stepNum}>3</span>
              <h3 className={styles.stepTitle}>Share feedback</h3>
              <div className={styles.stepText}>
                Tell us what works and what doesn’t through:
                <ul>
                  <li>Feedback Hub</li>
                  <li>Discord</li>
                  <li>Feature requests</li>
                  <li>Bug reports</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ── Why PM Pixel exists ────────────────────────────────────────── */}
        <section className={styles.section}>
          <p className={styles.eyebrow}>Why PM Pixel exists</p>
          <h2 className={styles.sectionTitle}>Built to close the readiness gap</h2>
          <p className={styles.sectionLead}>
            Many certification candidates struggle with the same questions. PM Pixel
            was created to answer them.
          </p>
          <div className={styles.grid2}>
            <div className={styles.card}>
              <p className={styles.eyebrow}>The struggle</p>
              <ul className={styles.challengeList}>
                {CHALLENGES.map((c) => <li key={c}>{c}</li>)}
              </ul>
            </div>
            <div className={styles.card}>
              <p className={styles.eyebrow}>How PM Pixel helps</p>
              <div className={styles.grid3} style={{ gap: '12px' }}>
                {FEATURES.map((f) => (
                  <div className={styles.featureCard} key={f.name}>
                    <span className={styles.featureIcon} aria-hidden="true">{f.icon}</span>
                    <span className={styles.featureName}>{f.name}</span>
                    <span className={styles.featureDesc}>{f.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Why we need testers ────────────────────────────────────────── */}
        <section className={styles.section}>
          <p className={styles.eyebrow}>Why we need testers</p>
          <h2 className={styles.sectionTitle}>This is an active beta program</h2>
          <p className={styles.sectionLead}>
            The goal is not simply bug testing. We are looking for thoughtful
            feedback on the experience itself:
          </p>
          <div className={styles.card}>
            <div className={styles.chips}>
              {FEEDBACK_AREAS.map((a) => <span className={styles.chip} key={a}>{a}</span>)}
            </div>
          </div>
        </section>

        {/* ── Community ──────────────────────────────────────────────────── */}
        <section className={styles.section}>
          <div className={styles.community}>
            <div>
              <p className={styles.eyebrow}>Community</p>
              <h2 className={styles.sectionTitle}>Join the conversation on Discord</h2>
              <ul className={styles.communityList}>
                <li>Ask questions</li>
                <li>Share study strategies</li>
                <li>Discuss certifications</li>
                <li>Follow product updates</li>
                <li>Connect with other learners</li>
              </ul>
              <a className={`${styles.btn} ${styles.btnDiscord}`} href={DISCORD_URL} target="_blank" rel="noopener noreferrer" onClick={trackDiscord}>
                💬 Join the Discord Community
              </a>
            </div>
            <CoachAvatar pose="cheer" heightPx={380} />
          </div>
        </section>

        {/* ── Founder ────────────────────────────────────────────────────── */}
        <section className={styles.section}>
          <div className={styles.card}>
            <div className={styles.founder}>
              <div className={styles.founderPhoto}>
                <span aria-hidden="true">BC</span>
                <img
                  src="/themes/pm-pixel/brand/founder-bryan.jpg"
                  alt="Bryan Campbell"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </div>
              <div>
                <h2 className={styles.founderName}>Bryan Campbell</h2>
                <ul className={styles.credList}>
                  {CREDENTIALS.map((c) => <li key={c}>{c}</li>)}
                </ul>
                <p className={styles.founderNote}>
                  PM Pixel began as a personal effort to improve the certification
                  preparation experience after years of training and mentoring
                  certification candidates. Thank you for helping shape the platform.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── Final CTA — historical: routes to the Wall, not signup ─────── */}
        <section className={styles.finalCta}>
          <h2 className={styles.finalTitle}>To everyone who helped build PM Pixel — thank you.</h2>
          <div className={styles.finalCtas}>
            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={openWall} data-testid="ft-visit-wall-footer">
              See the Founders Wall →
            </button>
            <a className={`${styles.btn} ${styles.btnDiscord}`} href={DISCORD_URL} target="_blank" rel="noopener noreferrer" onClick={trackDiscord}>
              💬 Join Discord
            </a>
          </div>
        </section>

      </div>
    </div>
  );
}
